/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type KeyboardEvent, type MouseEvent, useCallback, useRef } from 'react';

import { css, jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { mergeRefs } from 'use-callback-ref';

import { ButtonItem, LinkItem } from '@atlaskit/menu';
import type { ButtonItemProps, LinkItemProps } from '@atlaskit/menu/types';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { useMenuControl } from '../../../context';
import type { MenuItem, WrapperRenderProps } from '../../../types';
import { omitSafely } from '../../utils';
import { SkeletonItem } from '../SkeletonItem';

import ItemWrapper from './item-wrapper';

const textStyle = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		fontFamily: 'font.family.body',
		textOverflow: 'ellipsis',
		display: 'block',
	},
});

// Function to check if the element's text is truncated
function isTextClamped(labelElement: HTMLElement, boxElement: HTMLElement): boolean {
	return labelElement.scrollWidth > boxElement.scrollWidth;
}

const Item = forwardRef<HTMLAnchorElement | HTMLButtonElement, Omit<MenuItem, 'ref'>>(
	(
		{
			onClick,
			onContextMenu,
			isLoading,
			loadCreateContent: createContent,
			onClickCloseMenu = true,
			children,
			testId,
			isDisabled,
			href,
			rel,
			target,
			elemBefore,
			Wrapper,
		},
		forwardRef,
	) => {
		const { product, ...menuControl } = useMenuControl();
		const labelRef = useRef<HTMLElement>(null);

		const buttonClick = useCallback(
			(event: MouseEvent | KeyboardEvent) => {
				if (createContent) {
					menuControl.load(createContent);
				} else if (onClickCloseMenu) {
					menuControl.close();
				}

				onClick?.(event);
			},
			[menuControl, onClick, onClickCloseMenu, createContent],
		);

		const renderButton = useCallback(
			(props?: WrapperRenderProps) => {
				const { triggerProps } = props || {};
				const { ref: triggerRef, ...restTriggerProps } = triggerProps || {};
				const mergedRef = mergeRefs([
					...(forwardRef ? [forwardRef] : []),
					...(triggerRef ? [triggerRef] : []),
				]);

				const Icon = elemBefore;
				const baseProps = {
					testId,
					iconBefore: Icon ? <Icon label="" color={token('color.text.subtle')} /> : undefined,
					onClick: buttonClick,
					onContextMenu,
					className: 'custom-item-style',
					children: (
						<span css={textStyle}>
							<Text ref={labelRef} maxLines={1} color="color.text.subtle" weight="medium">
								{children}
							</Text>
						</span>
					),
				};

				const canTooltipAppear: (() => boolean) | undefined = () => {
					return labelRef.current && mergedRef.current
						? isTextClamped(labelRef.current, mergedRef.current)
						: false;
				};

				const itemProps: LinkItemProps = {
					...baseProps,
					...restTriggerProps,
					...(mergedRef && { ref: mergedRef }),
					href,
					rel,
					target,
					isDisabled,
				};

				const buttonItemProps: ButtonItemProps = omitSafely(
					itemProps,
					'href',
					'rel',
					'target',
					'role',
					'UNSAFE_shouldDisableRouterLink',
					'UNSAFE_isDraggable',
				);

				return (
					<Tooltip content={children} canAppear={canTooltipAppear}>
						{/* eslint-disable-next-line @atlassian/a11y/anchor-has-content -- See https://go/a11y-anchor-has-content for more details */}
						{href ? <LinkItem {...itemProps} /> : <ButtonItem {...buttonItemProps} />}
					</Tooltip>
				);
			},
			[
				testId,
				elemBefore,
				buttonClick,
				children,
				forwardRef,
				href,
				rel,
				isDisabled,
				target,
				onContextMenu,
			],
		);

		if (isLoading) {
			return <SkeletonItem testId={`loading-${testId}`} />;
		}

		if (['confluence', 'jira'].includes(product) && Wrapper) {
			return <Wrapper>{(props?: WrapperRenderProps) => renderButton(props)}</Wrapper>;
		}

		return renderButton();
	},
);

export default function MenuContentItem({ item }: { item: MenuItem }) {
	const { ref, ...props } = item;
	return (
		<ErrorBoundary fallback={null}>
			<ItemWrapper>
				<Item {...props} ref={ref} />
			</ItemWrapper>
		</ErrorBoundary>
	);
}
